import { z } from 'zod';
import { balanceAmountStringSchema, currencyKindSchema } from '../currency';
import { idempotencyKeySchema } from '../ids';
import { ITEM_CATEGORIES, itemDefinitionIdSchema } from '../inventory/definitions';
import { raritySchema } from '../rarity';

/**
 * The gacha's wire contract.
 *
 * ## What a request may never say
 *
 * No `accountId`: the puller is the authenticated session. No seed, no nonce,
 * no rarity, no definition, no pity counter and no price. A client that could
 * state any of those could state a Prismatic pet for zero Gold. The request
 * carries a banner, a currency, a size and an idempotency key, and every one
 * of those is validated against a server-side table.
 *
 * Every request schema is `.strict()`, so an unknown field is a refusal rather
 * than a silently ignored one - which is what stops `{ rarity: 'prismatic' }`
 * from being harmlessly ignored today and quietly honoured after a refactor.
 *
 * ## What a response carries
 *
 * Everything the reveal animation needs, resolved on the server: the display
 * name, the asset key, the category, the rarity, the level, a pet's stars, the
 * new instance id, and whether the guarantee produced it. The client draws
 * that; it never decides any of it.
 */

export const GACHA_CONTRACT_VERSION = 1 as const;

export const GACHA_BANNER_ROUTE = '/api/gacha/banner' as const;
export const GACHA_PULL_ROUTE = '/api/gacha/pull' as const;
export const GACHA_HISTORY_ROUTE = '/api/gacha/history' as const;

export const GACHA_HISTORY_PAGE_SIZE_DEFAULT = 20;
export const GACHA_HISTORY_PAGE_SIZE_MAX = 50;

export const gachaPullSizeSchema = z.union([z.literal(1), z.literal(10)]);

/* -------------------------------------------------------------------------- */
/*  Requests                                                                  */
/* -------------------------------------------------------------------------- */

export const gachaPullRequestSchema = z
  .object({
    bannerId: z.string().min(1).max(64),
    currency: currencyKindSchema,
    /** One or ten. Nothing else is a quantity this banner sells. */
    size: gachaPullSizeSchema,
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();
export type GachaPullRequest = z.infer<typeof gachaPullRequestSchema>;

export const gachaHistoryQuerySchema = z
  .object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(GACHA_HISTORY_PAGE_SIZE_MAX).optional(),
  })
  .strict();

/* -------------------------------------------------------------------------- */
/*  The banner, its odds and its pool                                         */
/* -------------------------------------------------------------------------- */

/** One item a band can produce, as the odds screen lists it. */
export const gachaPoolEntryWireSchema = z.object({
  definitionId: itemDefinitionIdSchema,
  name: z.string().min(1),
  category: z.enum(ITEM_CATEGORIES),
  rarity: raritySchema,
  assetKey: z.string().min(1).nullable(),
  /** A pet's rating; null for everything else. */
  stars: z.number().int().min(1).max(5).nullable(),
});

/**
 * One band: its chance, and everything in it.
 *
 * `points` travels beside the label so a player - or an auditor - can check the
 * arithmetic rather than trust a rounded percentage.
 */
export const gachaBandWireSchema = z.object({
  rarity: raritySchema,
  points: z.number().int().min(0),
  totalPoints: z.number().int().min(1),
  chanceLabel: z.string().min(1),
  items: z.array(gachaPoolEntryWireSchema),
});

export const gachaPriceWireSchema = z.object({
  size: gachaPullSizeSchema,
  gold: balanceAmountStringSchema,
  cash: balanceAmountStringSchema,
});

export const gachaBannerResponseSchema = z.object({
  contractVersion: z.literal(GACHA_CONTRACT_VERSION),
  bannerId: z.string().min(1),
  oddsVersion: z.string().min(1),
  prices: z.array(gachaPriceWireSchema),
  bands: z.array(gachaBandWireSchema),
  pity: z.object({
    /** How many rolls have passed without one of the three. */
    counter: z.number().int().min(0),
    threshold: z.number().int().min(1),
    /** The rarities the guarantee may produce, each with an equal third. */
    rarities: z.array(raritySchema),
  }),
  /** The caller's own spendable balances, so the panel needs one request. */
  balances: z.object({
    gold: balanceAmountStringSchema,
    cash: balanceAmountStringSchema,
  }),
});
export type GachaBannerResponse = z.infer<typeof gachaBannerResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  Results                                                                   */
/* -------------------------------------------------------------------------- */

/** One item the pull produced, with everything the reveal needs. */
export const gachaResultWireSchema = z.object({
  /** The real vault row. It is in the inventory the moment this is sent. */
  instanceId: z.string().uuid(),
  definitionId: itemDefinitionIdSchema,
  name: z.string().min(1),
  categoryLabel: z.string(),
  category: z.enum(ITEM_CATEGORIES),
  rarity: raritySchema,
  level: z.number().int().min(1),
  levelLabel: z.string().min(1),
  assetKey: z.string().min(1).nullable(),
  stars: z.number().int().min(1).max(5).nullable(),
  /** True when the guarantee produced this one, rather than the band table. */
  forcedByPity: z.boolean(),
});
export type GachaResultWire = z.infer<typeof gachaResultWireSchema>;

export const gachaPullResponseSchema = z.object({
  contractVersion: z.literal(GACHA_CONTRACT_VERSION),
  bannerId: z.string().min(1),
  oddsVersion: z.string().min(1),
  currency: currencyKindSchema,
  /** What was actually charged, from the server's own table. */
  cost: balanceAmountStringSchema,
  /** The balance AFTER the charge, so the panel never predicts one. */
  balance: balanceAmountStringSchema,
  results: z.array(gachaResultWireSchema),
  pityBefore: z.number().int().min(0),
  pityAfter: z.number().int().min(0),
  /** True when this answer came from a replayed key: nothing was charged. */
  replayed: z.boolean(),
  pulledAt: z.string(),
});
export type GachaPullResponse = z.infer<typeof gachaPullResponseSchema>;

/** One past pull, as the player's own history shows it. */
export const gachaHistoryEntryWireSchema = z.object({
  eventId: z.string().uuid(),
  bannerId: z.string().min(1),
  oddsVersion: z.string().min(1),
  currency: currencyKindSchema,
  size: gachaPullSizeSchema,
  cost: balanceAmountStringSchema,
  pityBefore: z.number().int().min(0),
  pityAfter: z.number().int().min(0),
  forcedAny: z.boolean(),
  results: z.array(gachaResultWireSchema),
  pulledAt: z.string(),
});

export const gachaHistoryResponseSchema = z.object({
  contractVersion: z.literal(GACHA_CONTRACT_VERSION),
  entries: z.array(gachaHistoryEntryWireSchema),
  nextCursor: z.string().nullable(),
});
export type GachaHistoryResponse = z.infer<typeof gachaHistoryResponseSchema>;
